import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Layers,
  Plus,
  BarChart3,
  TrendingUp,
  DollarSign,
  Globe,
  Layout,
  LogOut,
  Settings,
  ChevronDown,
  Trash2,
  Edit,
  Eye,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublisherDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [properties, setProperties] = useState([]);
  const [adZones, setAdZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [newProperty, setNewProperty] = useState({
    name: "",
    url: "",
    category: "news"
  });

  const [newZone, setNewZone] = useState({
    name: "",
    size: "300x250",
    floor_price: 1.0,
    position: "sidebar",
    demand_sources: ["open_auction", "direct_deals"]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, propertiesRes, zonesRes] = await Promise.all([
        axios.get(`${API}/analytics/publisher`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/ad-zones`)
      ]);
      setAnalytics(analyticsRes.data);
      setProperties(propertiesRes.data);
      setAdZones(zonesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/properties`, newProperty);
      setProperties([response.data, ...properties]);
      setShowPropertyModal(false);
      toast.success("Property created successfully!");
      setNewProperty({ name: "", url: "", category: "news" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create property");
    }
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    if (!selectedProperty) {
      toast.error("Please select a property first");
      return;
    }
    try {
      const response = await axios.post(`${API}/ad-zones?property_id=${selectedProperty}`, newZone);
      setAdZones([response.data, ...adZones]);
      setShowZoneModal(false);
      toast.success("Ad zone created successfully!");
      setNewZone({
        name: "",
        size: "300x250",
        floor_price: 1.0,
        position: "sidebar",
        demand_sources: ["open_auction", "direct_deals"]
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ad zone");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const copyAdTag = (zoneId) => {
    const tag = `<script src="${process.env.REACT_APP_BACKEND_URL}/api/ad-server/serve?zone_id=${zoneId}"></script>`;
    navigator.clipboard.writeText(tag);
    toast.success("Ad tag copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="publisher-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-emerald flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">MediaTNT</span>
              <span className="px-2 py-0.5 rounded bg-emerald/20 text-emerald text-xs font-medium">Publisher</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button 
                onClick={() => setShowPropertyModal(true)}
                className="bg-emerald hover:bg-emerald/90"
                data-testid="add-property-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-night-paper transition-colors">
                    <div className="w-8 h-8 rounded-full bg-emerald/20 flex items-center justify-center">
                      <span className="text-sm font-semibold text-emerald">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-night-paper border-night-subtle">
                  <DropdownMenuItem className="text-muted-foreground">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-rose" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white mb-1">
              Publisher Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your inventory and track revenue
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Revenue", value: `$${analytics?.total_revenue?.toFixed(2) || "0.00"}`, icon: DollarSign, color: "text-emerald" },
            { label: "Impressions", value: analytics?.total_impressions?.toLocaleString() || "0", icon: Eye, color: "text-primary" },
            { label: "Fill Rate", value: `${analytics?.avg_fill_rate || 0}%`, icon: TrendingUp, color: "text-yellow-500" },
            { label: "eCPM", value: `$${analytics?.avg_ecpm?.toFixed(2) || "0.00"}`, icon: BarChart3, color: "text-rose" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 rounded-lg bg-night-paper border border-night-subtle card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`stat-${stat.label.toLowerCase().replace(" ", "-")}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">{stat.label}</span>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className="font-heading text-2xl font-bold text-white">{stat.value}</div>
            </motion.div>
          ))}
        </div>

        {/* Properties Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Properties List */}
          <div className="rounded-lg bg-night-paper border border-night-subtle overflow-hidden">
            <div className="p-6 border-b border-night-subtle flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-white">Your Properties</h2>
              <span className="text-sm text-muted-foreground">{properties.length} properties</span>
            </div>

            {properties.length === 0 ? (
              <div className="p-8 text-center">
                <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">No properties yet</h3>
                <p className="text-muted-foreground mb-4">Add your first property to start monetizing</p>
                <Button onClick={() => setShowPropertyModal(true)} className="bg-emerald hover:bg-emerald/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Property
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-night-subtle">
                {properties.map((property) => (
                  <div 
                    key={property.id} 
                    className="p-4 hover:bg-night-subtle/50 transition-colors"
                    data-testid={`property-${property.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-white mb-1">{property.name}</div>
                        <div className="text-sm text-muted-foreground">{property.url}</div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            property.status === "active" ? "bg-emerald/20 text-emerald" : "bg-night-subtle text-muted-foreground"
                          }`}>
                            {property.status}
                          </span>
                          <span className="text-xs text-muted-foreground">{property.category}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-white">${property.total_revenue?.toFixed(2) || "0.00"}</div>
                        <div className="text-xs text-muted-foreground">revenue</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ad Zones */}
          <div className="rounded-lg bg-night-paper border border-night-subtle overflow-hidden">
            <div className="p-6 border-b border-night-subtle flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-white">Ad Zones</h2>
              <Button 
                size="sm"
                onClick={() => setShowZoneModal(true)}
                className="bg-emerald hover:bg-emerald/90"
                disabled={properties.length === 0}
                data-testid="add-zone-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Zone
              </Button>
            </div>

            {adZones.length === 0 ? (
              <div className="p-8 text-center">
                <Layout className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">No ad zones yet</h3>
                <p className="text-muted-foreground">Create ad zones to display ads on your properties</p>
              </div>
            ) : (
              <div className="divide-y divide-night-subtle">
                {adZones.map((zone) => (
                  <div 
                    key={zone.id} 
                    className="p-4 hover:bg-night-subtle/50 transition-colors"
                    data-testid={`zone-${zone.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-white mb-1">{zone.name}</div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs">
                            {zone.size}
                          </span>
                          <span className="text-xs text-muted-foreground">{zone.position}</span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-2">
                          Floor: ${zone.floor_price?.toFixed(2)} CPM
                        </div>
                      </div>
                      <div className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyAdTag(zone.id)}
                          className="border-night-subtle text-xs"
                          data-testid={`copy-tag-${zone.id}`}
                        >
                          Copy Tag
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-night-subtle">
                      <div>
                        <div className="text-xs text-muted-foreground">Impressions</div>
                        <div className="font-semibold text-white">{zone.impressions?.toLocaleString() || 0}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Fill Rate</div>
                        <div className="font-semibold text-white">{zone.fill_rate || 0}%</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Revenue</div>
                        <div className="font-semibold text-emerald">${zone.revenue?.toFixed(2) || "0.00"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* API Key Section */}
        <div className="rounded-lg bg-night-paper border border-night-subtle p-6">
          <h2 className="font-heading text-lg font-semibold text-white mb-4">Your API Key</h2>
          <div className="flex items-center gap-4">
            <div className="flex-1 p-3 rounded bg-night-bg border border-night-subtle font-mono text-sm text-muted-foreground">
              {user?.api_key || "No API key generated"}
            </div>
            <Button 
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(user?.api_key || "");
                toast.success("API key copied!");
              }}
              className="border-night-subtle"
              data-testid="copy-api-key"
            >
              Copy
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Use this API key for custom integrations with your ad server.
          </p>
        </div>
      </main>

      {/* Add Property Modal */}
      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <DialogContent className="bg-night-paper border-night-subtle">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Add Property</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateProperty} className="space-y-4">
            <div className="space-y-2">
              <Label>Property Name</Label>
              <Input
                value={newProperty.name}
                onChange={(e) => setNewProperty(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My Website"
                required
                className="bg-night-bg border-night-subtle"
                data-testid="property-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input
                type="url"
                value={newProperty.url}
                onChange={(e) => setNewProperty(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com"
                required
                className="bg-night-bg border-night-subtle"
                data-testid="property-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={newProperty.category}
                onValueChange={(value) => setNewProperty(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="property-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-night-paper border-night-subtle">
                  <SelectItem value="news">News & Media</SelectItem>
                  <SelectItem value="entertainment">Entertainment</SelectItem>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="lifestyle">Lifestyle</SelectItem>
                  <SelectItem value="sports">Sports</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPropertyModal(false)}
                className="border-night-subtle"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald hover:bg-emerald/90" data-testid="submit-property-btn">
                Add Property
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Zone Modal */}
      <Dialog open={showZoneModal} onOpenChange={setShowZoneModal}>
        <DialogContent className="bg-night-paper border-night-subtle">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Create Ad Zone</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateZone} className="space-y-4">
            <div className="space-y-2">
              <Label>Property</Label>
              <Select
                value={selectedProperty}
                onValueChange={setSelectedProperty}
              >
                <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="zone-property-select">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent className="bg-night-paper border-night-subtle">
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Zone Name</Label>
              <Input
                value={newZone.name}
                onChange={(e) => setNewZone(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Header Banner"
                required
                className="bg-night-bg border-night-subtle"
                data-testid="zone-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Size</Label>
                <Select
                  value={newZone.size}
                  onValueChange={(value) => setNewZone(prev => ({ ...prev, size: value }))}
                >
                  <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="zone-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-night-paper border-night-subtle">
                    <SelectItem value="728x90">728x90 (Leaderboard)</SelectItem>
                    <SelectItem value="300x250">300x250 (Medium Rectangle)</SelectItem>
                    <SelectItem value="336x280">336x280 (Large Rectangle)</SelectItem>
                    <SelectItem value="300x600">300x600 (Half Page)</SelectItem>
                    <SelectItem value="320x50">320x50 (Mobile Banner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Position</Label>
                <Select
                  value={newZone.position}
                  onValueChange={(value) => setNewZone(prev => ({ ...prev, position: value }))}
                >
                  <SelectTrigger className="bg-night-bg border-night-subtle" data-testid="zone-position-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-night-paper border-night-subtle">
                    <SelectItem value="header">Header</SelectItem>
                    <SelectItem value="sidebar">Sidebar</SelectItem>
                    <SelectItem value="in-content">In-Content</SelectItem>
                    <SelectItem value="footer">Footer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Floor Price (CPM)</Label>
              <Input
                type="number"
                value={newZone.floor_price}
                onChange={(e) => setNewZone(prev => ({ ...prev, floor_price: parseFloat(e.target.value) }))}
                min="0.01"
                step="0.01"
                required
                className="bg-night-bg border-night-subtle"
                data-testid="zone-floor-price-input"
              />
              <p className="text-xs text-muted-foreground">Minimum CPM you'll accept for this ad zone</p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowZoneModal(false)}
                className="border-night-subtle"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald hover:bg-emerald/90" data-testid="submit-zone-btn">
                Create Zone
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublisherDashboard;
